import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  Autocomplete,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import TabPanel from '../../../components/TabPanel';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
// Components
import BasicInformationTab from './BasicInformationTab';
import ProfessionlInfoTab from './ProfessionlInfoTab';
import EngagementHistory from './EngagementHistory';
import MDUsage from './MDUsage';
import AccountManagementTab from './AccountManagementTab';
import TicketTrackerTab from './TicketTrackerTab';
// API
import { getRepFilter, getDoctorFilter } from '../../../api/filterOptionsApi';
// Helpers
import {
  filterValue,
  filterRepOptions,
  filterDoctorv2Options,
} from '../../../helpers/filterFunctions';
// Context
import { useMdProfileContext } from '../context/useMdProfileContext';
import moment from 'moment';

export default function ViewMDProfileDialog() {
  const { viewProfile, setViewProfile } = useContext(useMdProfileContext);

  const modalDefaultFilterForm = {
    doctor_id: { label: '-- Choose MD --', value: null },
    rep_id: { label: 'ALL', value: null },
    year: moment().format('YYYY'),
  };
  const [activeTab, setActiveTab] = useState(0);
  // FILTER STATE
  const [modalFilterForm, setModalFilterForm] = useState(modalDefaultFilterForm);
  // DROPDOWN OPTIONS
  const [mdOptions, setMdOptions] = useState({ loading: false, data: [] });
  const [repOptions, setRepOptions] = useState({ loading: false, data: [] });

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    setViewProfile({ ...viewProfile, toggle: false });
  };

  const handleChangeDate = (value) => {
    setModalFilterForm({ ...modalFilterForm, year: moment(value).format('YYYY') });
  };

  const handleChange = (e, row, name) => {
    setModalFilterForm({ ...modalFilterForm, [name]: row });
  };

  const handleSwitchDoctor = (e, row, name) => {
    setModalFilterForm({ ...modalFilterForm, [name]: row });

    const doctorObj = {
      id: 7,
      md_id: row?.value,
      name: row?.label,
      specialty: 'Cardiology',
      primary_clinic: 'Saint Luke Medical Center',
      clinic_hour: '8:30am - 5:30pm',
      best_day_to_cover: 'Thursday, Friday',
    };
    setViewProfile({ ...viewProfile, toggle: true, doctor_id: row?.value, doctor: doctorObj });
  };

  const loadMDDropdown = async () => {
    setMdOptions({ ...mdOptions, loading: true });
    const response = await getDoctorFilter();
    const { code, data } = response.data;

    if (code === '200') {
      const dropdownValue = filterDoctorv2Options(data);
      setMdOptions({ ...mdOptions, loading: false, data: dropdownValue });
      setModalFilterForm({
        ...modalFilterForm,
        doctor_id: dropdownValue.find((row) => row.value === viewProfile.doctor_id),
      });
    }
  };

  const loadRepDropdown = async () => {
    setRepOptions({ ...repOptions, loading: true });
    const request = {
      md_id: [viewProfile.doctor_id],
      year: [modalFilterForm?.year],
    };
    const reponse = await getRepFilter(request);
    const { code, data } = reponse.data;
    if (code === '200') {
      const dropdownValue = filterRepOptions(data);
      setRepOptions({ ...repOptions, loading: false, data: dropdownValue });
    }
  };

  useEffect(() => {
    loadMDDropdown();
  }, []);

  useEffect(() => {
    loadRepDropdown();
  }, [viewProfile.doctor_id, modalFilterForm?.year]);

  return (
    <Dialog fullScreen open={viewProfile?.toggle} onClose={handleClose}>
      <DialogTitle>
        MD Profile
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box padding={4}>
          <Box>
            <Typography sx={{ fontWeight: 'bold' }} variant='h6' gutterBottom>
              MD Name: {viewProfile?.doctor?.name}
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }} variant='h6' gutterBottom>
              Specialty: {viewProfile?.doctor?.specialty}
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }} variant='h6' gutterBottom>
              Best Day to Cover: {viewProfile?.doctor?.best_day_to_cover}
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }} variant='h6' gutterBottom>
              Best Time to Cover: {viewProfile?.doctor?.clinic_hour}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} sm={12} md={12}>
              <Box sx={{ backgroundColor: '#fff' }} mt={2}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleChangeActiveTab}
                    aria-label='basic tabs example'
                  >
                    <Tab
                      label='Basic Information'
                      id='basic-info-tab'
                      aria-controls='basic-info-tab'
                    />
                    <Tab
                      label='Professional'
                      id='professional-info-tab'
                      aria-controls='professional-info-tab'
                    />
                    <Tab
                      label='Engagement History'
                      id='engagement-history-tab'
                      aria-controls='engagement-history-tab'
                    />
                    <Tab label='MD Usage' id='md-usage-tab' aria-controls='md-usage-tab' />

                    <Tab
                      label='Account Management'
                      id='account-management-tab'
                      aria-controls='account-management-tab'
                    />
                  </Tabs>
                </Box>

                <Box display='flex' justifyContent={'flex-end'} p={2} gap={2}>
                  {activeTab === 2 || activeTab === 4 ? (
                    <>
                      <DesktopDatePicker
                        label='Year'
                        views={['year']}
                        value={modalFilterForm?.year}
                        fullWidth
                        onChange={(e) => handleChangeDate(e)}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => <TextField size='small' {...params} />}
                        pr={2}
                      />
                    </>
                  ) : (
                    ''
                  )}

                  {activeTab === 2 ? (
                    <>
                      <Autocomplete
                        disablePortal
                        id='rep_id'
                        disableClearable
                        fullWidth
                        loading={repOptions?.loading}
                        options={repOptions?.data}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => <TextField {...params} label='(KASS/SSAR)' />}
                        size='small'
                        value={modalFilterForm?.rep_id}
                        onChange={(e, newValue) => handleChange(e, newValue, 'rep_id')}
                        sx={{ backgroundColor: '#fff', width: '20%' }}
                        pr={2}
                      />
                    </>
                  ) : (
                    ''
                  )}

                  <Autocomplete
                    disablePortal
                    id='doctor_id'
                    disableClearable
                    fullWidth
                    loading={mdOptions?.loading}
                    options={mdOptions?.data}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField {...params} label='MD' />}
                    size='small'
                    value={modalFilterForm?.doctor_id}
                    onChange={(e, newValue) => handleSwitchDoctor(e, newValue, 'doctor_id')}
                    sx={{ backgroundColor: '#fff', width: '20%' }}
                  />
                </Box>
                <TabPanel value={activeTab} index={0}>
                  <BasicInformationTab />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <ProfessionlInfoTab />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  <EngagementHistory modalFilterForm={modalFilterForm} />
                </TabPanel>
                <TabPanel value={activeTab} index={3}>
                  <MDUsage />
                </TabPanel>
                <TabPanel value={activeTab} index={4}>
                  <AccountManagementTab modalFilterForm={modalFilterForm} />
                </TabPanel>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
